import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle, ShieldCheck, HandCoins, Briefcase, Plus, Trash2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS_PREST = ["Transport", "Travaux Publics", "Location d'engins", "Main-d'œuvre", "Autre"];
const MODES = ["Forfait", "Horaire", "Journalier", "Au km", "À la tonne", "Au m³", "À l'unité"];
const UNITES = ["km", "h", "jour", "t", "m³", "u", "forfait"];
const TVA_OPTS = ["8,5 %", "2,1 %", "0 % (exonéré)"];

const EMPTY_PREST = { categorie: "Transport", prestation: "", description: "", mode: "Forfait", unite: "km", tarif_ht: "", tva: "8,5 %", conditions: "" };
const EMPTY_OPT = { option: "", description: "", mode: "Forfait", unite: "u", tarif_ht: "", conditions: "" };

export default function DevenirPrestataire() {
  const [form, setForm] = useState({ raison_sociale: "", contact_nom: "", contact_fonction: "", telephone: "", email: "", siret: "", tva_intra: "", activite: "", zones: "" });
  const [prestations, setPrestations] = useState([{ ...EMPTY_PREST }]);
  const [options, setOptions] = useState([]);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrest = (i, k, v) => setPrestations((p) => { const c = [...p]; c[i] = { ...c[i], [k]: v }; return c; });
  const setOpt = (i, k, v) => setOptions((p) => { const c = [...p]; c[i] = { ...c[i], [k]: v }; return c; });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/public/prestataire`, {
        ...form,
        prestations: prestations.filter((p) => p.prestation.trim()),
        options: options.filter((o) => o.option.trim()),
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de l'envoi. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const selCls = "h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold w-full";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background" data-testid="prestataire-page">
      <header className="bg-teca-900 border-b-4 border-amber-500 px-4 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <img src="/logo-teca.png" alt="TECA Solutions TP" className="h-12 w-auto bg-white rounded-lg px-2 py-1" />
          <div>
            <div className="font-display text-xl font-bold text-white uppercase">TECA <span className="text-amber-500">Solutions TP</span></div>
            <div className="text-[11px] text-slate-300 uppercase tracking-widest">Espace Prestataires</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-8 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-teca-900 dark:text-foreground">
            Demande de tarifs prestataires
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
            Collecte normalisée — intégration simulateur & gestion TECA SOLUTIONS TP. Rejoignez le réseau : transport, TP, location d'engins, main-d'œuvre.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          {[
            { icon: CheckCircle, t: "Sans engagement", d: "Inscription gratuite et sans obligation." },
            { icon: ShieldCheck, t: "Données maîtrisées", d: "Vous définissez vos tarifs et conditions." },
            { icon: HandCoins, t: "Outil professionnel", d: "Simple, structuré et efficace." },
          ].map((b) => (
            <div key={b.t} className="bg-card rounded-xl border border-border p-4 text-center">
              <b.icon className="h-8 w-8 text-teca-700 dark:text-amber-500 mx-auto mb-2" />
              <div className="font-bold text-sm">{b.t}</div>
              <div className="text-xs text-muted-foreground mt-1">{b.d}</div>
            </div>
          ))}
        </div>

        <div className="bg-card rounded-xl border border-border p-4">
          <div className="font-bold text-sm mb-3">Types de prestations recherchées</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[["Transport", "Camions, semi-remorques, bennes"], ["Travaux Publics", "Terrassement, VRD, assainissement"],
              ["Location d'engins", "Pelles, chargeuses, compacteurs"], ["Main-d'œuvre", "Conducteurs, manœuvres, chefs d'équipe"]].map(([t, d]) => (
              <div key={t} className="bg-muted rounded-lg p-3"><div className="font-bold">{t}</div><div className="text-muted-foreground">{d}</div></div>
            ))}
          </div>
        </div>

        {done ? (
          <div className="bg-emerald-50 dark:bg-emerald-950 border-2 border-emerald-500 rounded-2xl p-6 text-center" data-testid="prestataire-success">
            <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <div className="font-display text-2xl font-bold uppercase text-emerald-800 dark:text-emerald-200">Demande envoyée !</div>
            <p className="text-sm mt-2">L'équipe TECA SOLUTIONS TP étudiera vos tarifs et vous recontactera pour la qualification (RNE, RC Pro, attestations). Vos tarifs restent confidentiels jusqu'à validation.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-card rounded-2xl border border-border p-5 sm:p-6 space-y-5 shadow-sm" data-testid="prestataire-form">
            <a data-testid="prest-pdf-download" href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/formulaire-prestataire`}
              target="_blank" rel="noreferrer"
              className="flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-teca-800 text-teca-900 dark:text-foreground font-bold text-sm hover:bg-muted transition-colors">
              <Download className="h-4 w-4" /> Télécharger le formulaire PDF (version papier)
            </a>

            <div>
              <div className="flex items-center gap-2 font-display font-bold text-lg uppercase text-teca-900 dark:text-foreground mb-3">
                <Briefcase className="h-5 w-5 text-amber-600" /> 1. Informations prestataire
              </div>
              <div className="space-y-3">
                <Input data-testid="prest-raison" required placeholder="Entreprise *" value={form.raison_sociale} onChange={(e) => set("raison_sociale", e.target.value)} className="py-3.5" />
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid="prest-contact" required placeholder="Contact principal *" value={form.contact_nom} onChange={(e) => set("contact_nom", e.target.value)} />
                  <Input data-testid="prest-fonction" placeholder="Fonction (Gérant, Directeur…)" value={form.contact_fonction} onChange={(e) => set("contact_fonction", e.target.value)} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid="prest-telephone" required placeholder="Téléphone *" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} />
                  <Input data-testid="prest-email" type="email" required placeholder="E-mail *" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <select data-testid="prest-activite" required value={form.activite} onChange={(e) => set("activite", e.target.value)} className={selCls}>
                  <option value="">Sélectionnez votre activité *</option>
                  {CATS_PREST.map((c) => <option key={c}>{c}</option>)}
                </select>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid="prest-siret" placeholder="SIRET" value={form.siret} onChange={(e) => set("siret", e.target.value)} />
                  <Input data-testid="prest-tva" placeholder="TVA intracommunautaire" value={form.tva_intra} onChange={(e) => set("tva_intra", e.target.value)} />
                </div>
                <Input data-testid="prest-zones" placeholder="Zones d'intervention (communes)" value={form.zones} onChange={(e) => set("zones", e.target.value)} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="font-display font-bold text-lg uppercase text-teca-900 dark:text-foreground">2. Prestations & tarification</div>
                <Button type="button" data-testid="prest-add-prestation" variant="outline" className="h-10 font-bold"
                  onClick={() => setPrestations((p) => [...p, { ...EMPTY_PREST }])}>
                  <Plus className="h-4 w-4 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="space-y-3">
                {prestations.map((p, i) => (
                  <div key={i} className="bg-muted/60 rounded-xl p-3 space-y-2" data-testid={`prestation-${i}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold uppercase text-muted-foreground">Prestation {i + 1}</span>
                      {prestations.length > 1 && (
                        <button type="button" data-testid={`prestation-delete-${i}`} onClick={() => setPrestations((x) => x.filter((_, k) => k !== i))} className="text-red-500 p-1"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select data-testid={`prestation-categorie-${i}`} value={p.categorie} onChange={(e) => setPrest(i, "categorie", e.target.value)} className={selCls}>
                        {CATS_PREST.map((c) => <option key={c}>{c}</option>)}
                      </select>
                      <Input data-testid={`prestation-nom-${i}`} placeholder="Nom de la prestation" value={p.prestation} onChange={(e) => setPrest(i, "prestation", e.target.value)} />
                    </div>
                    <Input data-testid={`prestation-description-${i}`} placeholder="Description détaillée" value={p.description} onChange={(e) => setPrest(i, "description", e.target.value)} />
                    <div className="grid grid-cols-2 gap-2">
                      <select data-testid={`prestation-mode-${i}`} value={p.mode} onChange={(e) => setPrest(i, "mode", e.target.value)} className={selCls}>
                        {MODES.map((m) => <option key={m}>{m}</option>)}
                      </select>
                      <select data-testid={`prestation-unite-${i}`} value={p.unite} onChange={(e) => setPrest(i, "unite", e.target.value)} className={selCls}>
                        {UNITES.map((u) => <option key={u}>{u}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid={`prestation-tarif-${i}`} type="number" step="any" min="0" placeholder="Tarif HT (€)" value={p.tarif_ht} onChange={(e) => setPrest(i, "tarif_ht", e.target.value)} />
                      <select data-testid={`prestation-tva-${i}`} value={p.tva} onChange={(e) => setPrest(i, "tva", e.target.value)} className={selCls}>
                        {TVA_OPTS.map((t) => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <Input data-testid={`prestation-conditions-${i}`} placeholder="Conditions (ex : Min 2h, zone…)" value={p.conditions} onChange={(e) => setPrest(i, "conditions", e.target.value)} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="font-display font-bold text-lg uppercase text-teca-900 dark:text-foreground">3. Services complémentaires / options</div>
                <Button type="button" data-testid="prest-add-option" variant="outline" className="h-10 font-bold"
                  onClick={() => setOptions((p) => [...p, { ...EMPTY_OPT }])}>
                  <Plus className="h-4 w-4 mr-1" /> Ajouter
                </Button>
              </div>
              <div className="space-y-3">
                {options.map((o, i) => (
                  <div key={i} className="bg-muted/60 rounded-xl p-3 space-y-2" data-testid={`option-${i}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-bold uppercase text-muted-foreground">Option {i + 1}</span>
                      <button type="button" data-testid={`option-delete-${i}`} onClick={() => setOptions((x) => x.filter((_, k) => k !== i))} className="text-red-500 p-1"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input data-testid={`option-nom-${i}`} placeholder="Option (ex : camion grue)" value={o.option} onChange={(e) => setOpt(i, "option", e.target.value)} />
                      <Input data-testid={`option-tarif-${i}`} type="number" step="any" min="0" placeholder="Tarif HT (€)" value={o.tarif_ht} onChange={(e) => setOpt(i, "tarif_ht", e.target.value)} />
                    </div>
                    <Input data-testid={`option-conditions-${i}`} placeholder="Conditions" value={o.conditions} onChange={(e) => setOpt(i, "conditions", e.target.value)} />
                  </div>
                ))}
                {options.length === 0 && <p className="text-xs text-muted-foreground">Aucune option — cliquez « Ajouter » si vous proposez des services complémentaires.</p>}
              </div>
            </div>

            {error && <div data-testid="prest-error" className="text-sm font-semibold text-red-600">{error}</div>}
            <Button data-testid="prest-submit" type="submit" disabled={loading}
              className="w-full h-14 text-base font-bold bg-teca-800 hover:bg-teca-700 text-white">
              {loading ? "ENVOI…" : "ENVOYER MA DEMANDE DE TARIFS"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              Vos données et tarifs restent confidentiels et ne sont utilisés que pour la qualification (RGPD — politique de confidentialité TECA).
            </p>
          </form>
        )}

        <div className="text-center text-xs text-muted-foreground">
          © 2026 TECA SOLUTIONS TP — Tous droits réservés — <Link to="/login" className="font-bold text-amber-700 hover:underline">Accès équipe</Link>
        </div>
      </main>
    </div>
  );
}
